import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Link, Paper, Stack, TextField, Typography,
} from '@mui/material';
import type { OrderMessage } from '@eprintsmart/shared';
import {
  orderMessageFileUrl, orderMessageUploadUrl, postOrderMessage, watchOrderMessages,
} from '../services/orders';

/**
 * The conversation with the customer, on the order.
 *
 * Its own component rather than another block inside OrderDetail.tsx, which is
 * already 887 lines: this one holds live state and an upload, and the panels
 * around it are static reads.
 *
 * WHAT THE CUSTOMER SEES. Everything here, immediately, on their own order
 * page — there is no internal-note mode. If a private note is ever wanted it
 * needs its own field and its own colour, because a staff-only message that
 * merely LOOKS different from a customer-visible one is the kind of thing that
 * gets sent to a customer by accident.
 */
export function OrderConversation({ orderId, customerEmail }: {
  orderId: string;
  customerEmail?: string;
}) {
  const [messages, setMessages] = useState<OrderMessage[] | null>(null);
  const [body, setBody] = useState('');
  const [pending, setPending] = useState<NonNullable<OrderMessage['attachments']>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => watchOrderMessages(orderId, setMessages), [orderId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages?.length]);

  const send = async () => {
    if (!body.trim() && pending.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await postOrderMessage({ orderId, body, ...(pending.length ? { attachments: pending } : {}) });
      setBody('');
      setPending([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not send.');
    } finally {
      setBusy(false);
    }
  };

  const attach = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const { uploadUrl, storagePath } = await orderMessageUploadUrl({
        orderId,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      // Straight to Storage on a signed URL — the file never passes through a
      // function, which is what keeps a 90MB artwork off a 512MiB instance.
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      setPending((prev) => [...prev, {
        path: storagePath,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        bytes: file.size,
      }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That file did not upload.');
    } finally {
      setBusy(false);
    }
  };

  const open = async (path: string) => {
    try {
      window.open(await orderMessageFileUrl(orderId, path), '_blank', 'noopener');
    } catch {
      setError('That file could not be opened.');
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Conversation with the customer</Typography>
      <Divider sx={{ mb: 1.5 }} />

      {!customerEmail && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This order has no email address, so the customer will not be told you have written.
          They will only see it if they open their order page.
        </Alert>
      )}

      {messages === null ? (
        <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={22} /></Stack>
      ) : messages.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Nothing yet. Anything you write here appears on the customer&rsquo;s own order page,
          and they are emailed a link to it.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2, maxHeight: 420, overflowY: 'auto' }}>
          {messages.map((m) => (
            <Paper
              key={m.id}
              variant="outlined"
              sx={{ p: 1.25, bgcolor: m.from === 'staff' ? 'action.hover' : 'background.paper' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip
                  size="small"
                  label={m.from === 'staff' ? 'You' : 'Customer'}
                  color={m.from === 'staff' ? 'primary' : 'default'}
                />
                {/* Only ever 'app' today. Shown so the day a reply arrives by
                    email it is obvious where it came from. */}
                {m.source === 'email' && <Chip size="small" variant="outlined" label="by email" />}
                <Typography variant="caption" color="text.secondary">
                  {new Date(Number(m.at)).toLocaleString()}
                </Typography>
              </Stack>
              {m.body && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
              )}
              {m.attachments?.map((a) => (
                <Box key={a.path} sx={{ mt: 0.5 }}>
                  <Link component="button" variant="body2" onClick={() => open(a.path)}>
                    {a.filename}
                  </Link>{' '}
                  <Typography variant="caption" color="text.secondary">
                    {(a.bytes / 1024).toFixed(0)} KB
                  </Typography>
                </Box>
              ))}
            </Paper>
          ))}
          <div ref={endRef} />
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <TextField
        multiline
        minRows={3}
        fullWidth
        size="small"
        placeholder="Write to the customer…"
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, 4000))}
        disabled={busy}
      />

      {pending.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
          {pending.map((a) => (
            <Chip
              key={a.path}
              size="small"
              label={a.filename}
              onDelete={() => setPending((prev) => prev.filter((x) => x.path !== a.path))}
            />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <Button variant="contained" size="small" onClick={send} disabled={busy || (!body.trim() && pending.length === 0)}>
          Send
        </Button>
        <Button component="label" size="small" disabled={busy}>
          Attach
          <input
            type="file"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void attach(f); e.target.value = ''; }}
          />
        </Button>
        {busy && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{body.length}/4000</Typography>
      </Stack>
    </Paper>
  );
}
